import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart3,
  DollarSign,
  Calendar,
  Search,
  TrendingUp,
  Award,
  Clock,
  ArrowRight,
  FlaskConical,
  MapPin,
  Wifi,
  Sparkles,
  ChevronRight,
} from 'lucide-react';
import { SCORE_TIERS } from '@apex/shared';
import { api } from '@/lib/api';
import { useAuthStore } from '@/lib/store';

function getTier(score: number) {
  return SCORE_TIERS.find((t) => score >= t.min && score <= t.max) ?? SCORE_TIERS[SCORE_TIERS.length - 1];
}

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

interface Stats {
  reliability_score: number;
  total_completed: number;
  total_earned: number;
  upcoming_appointments: number;
  active_studies_available: number;
}

interface StudyPreview {
  id: string;
  title: string;
  compensation_amount: number;
  compensation_type: string;
  duration_minutes: number;
  location: string | null;
  is_remote: boolean;
  category: string | null;
  researcher_name: string;
}

interface AppointmentPreview {
  id: string;
  study_title: string;
  slot_start_time: string;
  slot_end_time: string;
  study_location: string | null;
  study_is_remote: boolean;
  study_compensation_amount: number;
  study_compensation_type: string;
  status: string;
}

export default function StudentDashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<Stats | null>(null);
  const [studies, setStudies] = useState<StudyPreview[]>([]);
  const [appointments, setAppointments] = useState<AppointmentPreview[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [statsData, studiesData, appointmentsData] = await Promise.all([
          api.getStudentStats(),
          api.getAvailableStudies({ per_page: 4 }),
          api.getMyAppointments({ status: 'booked' }),
        ]);
        if (!mounted) return;
        setStats(statsData);
        setStudies(studiesData.data ?? []);
        setAppointments((appointmentsData.data ?? []).slice(0, 3));
      } catch {
        // fail silently
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const score = stats?.reliability_score ?? user?.reliability_score ?? 3.0;
  const tier = getTier(score);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-[3px] border-gray-200 dark:border-gray-800 border-t-brand-500 rounded-full animate-spin" />
          <p className="text-sm text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div initial="hidden" animate="visible" className="space-y-6">
      {/* Greeting */}
      <motion.div variants={fadeUp} custom={0}>
        <h1 className="page-header">
          Welcome back, {user?.first_name}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Here's what's happening with your research participation
        </p>
      </motion.div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Reliability Score */}
        <motion.div variants={fadeUp} custom={1} className="stat-card relative overflow-hidden col-span-2 lg:col-span-1">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl opacity-20" style={{ backgroundColor: tier.color }} />
          <div className="relative">
            <div className="flex items-center gap-2 mb-1">
              <Award className="w-4 h-4" style={{ color: tier.color }} />
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Reliability</span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-3xl font-bold" style={{ color: tier.color }}>{score.toFixed(1)}</span>
              <span className="text-xs text-gray-400">/ 5.0</span>
            </div>
            <span className="text-xs font-semibold mt-1 inline-block" style={{ color: tier.color }}>
              {tier.emoji} {tier.label}
            </span>
          </div>
        </motion.div>

        {/* Total Earned */}
        <motion.div variants={fadeUp} custom={2} className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-success-50 dark:bg-green-900/20 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-success-500" />
            </div>
          </div>
          <span className="text-2xl font-bold">${(stats?.total_earned ?? 0).toFixed(0)}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Total earned</span>
        </motion.div>

        {/* Completed */}
        <motion.div variants={fadeUp} custom={3} className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-900/20 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-brand-500" />
            </div>
          </div>
          <span className="text-2xl font-bold">{stats?.total_completed ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Studies completed</span>
        </motion.div>

        {/* Upcoming */}
        <motion.div variants={fadeUp} custom={4} className="stat-card">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-accent-50 dark:bg-accent-900/20 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-accent-500" />
            </div>
          </div>
          <span className="text-2xl font-bold">{stats?.upcoming_appointments ?? 0}</span>
          <span className="text-xs text-gray-500 dark:text-gray-400">Upcoming</span>
        </motion.div>
      </div>

      {/* Two-column: Upcoming Appointments + Available Studies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming Appointments */}
        <motion.div variants={fadeUp} custom={5} className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-header">Upcoming</h2>
            <Link to="/appointments" className="text-sm font-medium text-brand-500 hover:text-brand-600 flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {appointments.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No upcoming appointments</p>
              <Link to="/studies" className="text-sm text-brand-500 hover:text-brand-600 mt-2 inline-block">
                Browse available studies
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {appointments.map((apt) => (
                <div key={apt.id} className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-brand-50 dark:bg-brand-900/20 flex flex-col items-center justify-center">
                    <span className="text-[10px] font-bold text-brand-500 uppercase">
                      {new Date(apt.slot_start_time).toLocaleDateString('en-US', { month: 'short' })}
                    </span>
                    <span className="text-lg font-bold text-brand-600 dark:text-brand-400 leading-none">
                      {new Date(apt.slot_start_time).getDate()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{apt.study_title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(apt.slot_start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                      </span>
                      <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
                      {apt.study_is_remote ? (
                        <span className="flex items-center gap-1 text-xs text-accent-500"><Wifi className="w-3 h-3" /> Remote</span>
                      ) : (
                        <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 truncate"><MapPin className="w-3 h-3" /> {apt.study_location}</span>
                      )}
                    </div>
                  </div>
                  <span className="badge-success text-xs">${apt.study_compensation_amount}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Available Studies Preview */}
        <motion.div variants={fadeUp} custom={6} className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="section-header">Available Studies</h2>
            <Link to="/studies" className="text-sm font-medium text-brand-500 hover:text-brand-600 flex items-center gap-1">
              Browse all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {studies.length === 0 ? (
            <div className="text-center py-8">
              <Search className="w-10 h-10 text-gray-300 dark:text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">No studies available right now</p>
              <p className="text-xs text-gray-400 mt-1">Check back soon!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {studies.map((study) => (
                <Link
                  key={study.id}
                  to={`/studies/${study.id}`}
                  className="flex items-center gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500/10 to-accent-500/10 flex items-center justify-center">
                    <FlaskConical className="w-5 h-5 text-brand-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">{study.title}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-gray-500 dark:text-gray-400">{study.researcher_name}</span>
                      {study.category && (
                        <>
                          <span className="text-xs text-gray-300 dark:text-gray-600">|</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{study.category}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className="text-sm font-bold text-success-500">${study.compensation_amount}</span>
                    <p className="text-[10px] text-gray-400">{study.duration_minutes} min</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div variants={fadeUp} custom={7} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link
          to="/studies"
          className="card p-5 flex items-center gap-4 group hover:border-brand-200 dark:hover:border-brand-800 transition-colors"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-600 flex items-center justify-center shadow-lg shadow-brand-500/20">
            <Search className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Find Studies</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Browse {stats?.active_studies_available ?? 0} available studies</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-brand-500 group-hover:translate-x-1 transition-all" />
        </Link>

        <Link
          to="/profile"
          className="card p-5 flex items-center gap-4 group hover:border-accent-200 dark:hover:border-accent-800 transition-colors"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center shadow-lg shadow-accent-500/20">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold">Improve Your Profile</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Better profiles get more matches</p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-300 dark:text-gray-600 group-hover:text-accent-500 group-hover:translate-x-1 transition-all" />
        </Link>
      </motion.div>
    </motion.div>
  );
}
